import React, {useCallback, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import Popup from 'components/Popup'
import {useTimer, useViewport} from '@rambler-id/react'
import {pluralize} from '@rambler-tech/pluralize'
import {Button, Spinner} from 'rambler-ui'
import styles from './styles.css'

const THREE_DAYS_IN_MS = 3 * 24 * 60 * 60 * 1000
const ONE_MINUTE_IN_MS = 60 * 1000
const MOBILE_SPINNER_SIZE = 4
const DESKTOP_SPINNER_SIZE = 3

const LAST_CONFIRMATION_EMAIL_DATE = 'lastConfirmationEmailDate'

export default function UnconfirmedEmail({
  onResolve,
  unconfirmedEmail,
  ...props
}) {
  const {isMobile} = useViewport()
  const {creationTime, accountId: email} = unconfirmedEmail

  const blockDateFormatted = useMemo(() => {
    const estimatedBlockDate = new Date(creationTime / 1000 + THREE_DAYS_IN_MS)

    const minutes = estimatedBlockDate.getMinutes().toString().padStart(2, '0')
    const hours = estimatedBlockDate.getHours().toString().padStart(2, '0')

    return `${estimatedBlockDate.toLocaleDateString('ru-RU', {
      timeZone: 'UTC',
      month: 'long',
      day: '2-digit'
    })} в ${hours}:${minutes}`
  }, [creationTime])

  const {startTimer, stopTimer, remainingTime} = useTimer()

  useEffect(() => {
    let lastConfirmationEmailDate = Number(
      localStorage.getItem(LAST_CONFIRMATION_EMAIL_DATE)
    )

    if (lastConfirmationEmailDate) {
      const currentTime = +new Date()

      if (currentTime - lastConfirmationEmailDate < ONE_MINUTE_IN_MS) {
        startTimer(
          Math.trunc(
            (ONE_MINUTE_IN_MS - currentTime + lastConfirmationEmailDate) / 1000
          )
        )
      } else {
        localStorage.removeItem(LAST_CONFIRMATION_EMAIL_DATE)
      }
    }

    return () => remainingTime && stopTimer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendConfirmationEmail = useCallback(() => {
    const currentTime = +new Date()

    localStorage.setItem(LAST_CONFIRMATION_EMAIL_DATE, currentTime)

    startTimer(ONE_MINUTE_IN_MS / 1000)

    onResolve()
  }, [onResolve, startTimer])

  return (
    <Popup
      {...props}
      title="Подтвердите почтовый адрес"
      showClose={false}
      closeOnEsc={false}
      closeOnClickOutside={false}
      okButton={
        <div className={styles.footer}>
          <Button
            type="primary"
            onClick={sendConfirmationEmail}
            disabled={!!remainingTime}
            className={styles.button}
            block>
            Отправить письмо еще раз
          </Button>
          {!!remainingTime && (
            <div className={styles.time}>
              Через {pluralize(remainingTime, 'секунду', 'секунды', 'секунд')}
              <Spinner
                color="currentColor"
                inline
                size={isMobile ? MOBILE_SPINNER_SIZE : DESKTOP_SPINNER_SIZE}
                className={styles.spinner}
              />
            </div>
          )}
        </div>
      }>
      <p>
        <b>Ваш профиль заблокируется {blockDateFormatted},</b> если вы не
        подтвердите адрес электронной почты.
      </p>
      <p>
        Чтобы подтвердить почту, перейдите по ссылке из письма от Rambler&amp;Co
        ID, которое пришло вам на адрес <b>{email}</b>
      </p>
      <p>
        Если вы не получили письмо, запросите его повторно или проверьте папку
        «Спам».
      </p>
    </Popup>
  )
}

UnconfirmedEmail.propTypes = {
  onResolve: PropTypes.func
}
